import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { SupportedTokenName } from "@mezo/sdk"
import { Button, ButtonProps } from "@mezo-org/mezo-clay"
import { ReactNode } from "@tanstack/react-router"
import { Address } from "viem"

import { useApproveERC20Token } from "../hooks"
import { getCurrencySymbol } from "../utils"

import ProgressSteps, { NumberedStep } from "./ProgressSteps"

type ApproveAndCallProgressStepProps = {
  token: SupportedTokenName
  callTransactionStepTitle: string
}
type ApproveAndCallProgressStepsProviderProps = {
  callTransactionCallback: () => Promise<void>
  token: Address
  spender: Address
  amountToApprove: bigint
  children: ReactNode
  chainId: number
  errorChildren?: ReactNode
}

type ApproveAndCallProgressStepsContextValue = {
  onSubmitStep: () => Promise<void>
  isFetchingApprovedAmount: boolean
  isApprovalTxPending: boolean
  isApproveTxConfirmed: boolean
  isWaitingForApproveTxReceipt: boolean
  currentStep: number
  approvedAmount: bigint
  amountToApprove: bigint
}

const ApproveAndCallProgressStepsContext =
  createContext<ApproveAndCallProgressStepsContextValue>({
    onSubmitStep: () => Promise.resolve(),
    isFetchingApprovedAmount: false,
    isApprovalTxPending: false,
    isApproveTxConfirmed: false,
    isWaitingForApproveTxReceipt: false,
    approvedAmount: 0n,
    amountToApprove: 0n,
    currentStep: 0,
  })

export const useApproveAndCallProgressStepsContext = () => {
  const context = useContext(ApproveAndCallProgressStepsContext)

  if (!context)
    throw new Error(
      "ApproveAndCallProgressStepsContext used outside ApproveAndCallProgressStepsProvider component",
    )

  return context
}

export function ApproveAndCallProgressStepsProvider({
  callTransactionCallback,
  token,
  spender,
  amountToApprove,
  children,
  chainId,
  errorChildren,
}: ApproveAndCallProgressStepsProviderProps) {
  const [step, setStep] = useState(0)

  const {
    approvedAmount,
    approve,
    isFetchingApprovedAmount,
    isError,
    isPending: isApprovalTxPending,
    isTxConfirmed: isApproveTxConfirmed,
    isWaitingForTxReceipt: isWaitingForApproveTxReceipt,
  } = useApproveERC20Token(token, spender, chainId)

  useEffect(() => {
    if (approvedAmount && approvedAmount >= amountToApprove && step === 0) {
      setStep(1)
    }
  }, [approvedAmount, amountToApprove, step, isApproveTxConfirmed])

  const onSubmitStep = useCallback(async () => {
    if (approvedAmount! < amountToApprove) {
      await approve(amountToApprove)
      return
    }

    await callTransactionCallback()
  }, [callTransactionCallback, amountToApprove, approve, approvedAmount])

  const contextValue = useMemo<ApproveAndCallProgressStepsContextValue>(
    () => ({
      currentStep: step,
      isFetchingApprovedAmount,
      isApprovalTxPending,
      isApproveTxConfirmed,
      isWaitingForApproveTxReceipt,
      onSubmitStep,
      amountToApprove,
      approvedAmount: approvedAmount ?? 0n,
    }),
    [
      isFetchingApprovedAmount,
      isApprovalTxPending,
      isApproveTxConfirmed,
      isWaitingForApproveTxReceipt,
      onSubmitStep,
      step,
      amountToApprove,
      approvedAmount,
    ],
  )

  return (
    <ApproveAndCallProgressStepsContext.Provider value={contextValue}>
      {isError && errorChildren ? errorChildren : children}
    </ApproveAndCallProgressStepsContext.Provider>
  )
}

export default function ApproveAndCallProgressSteps({
  token,
  callTransactionStepTitle,
}: ApproveAndCallProgressStepProps) {
  const {
    currentStep,
    approvedAmount,
    amountToApprove,
    isApprovalTxPending,
    isFetchingApprovedAmount,
    isWaitingForApproveTxReceipt,
  } = useApproveAndCallProgressStepsContext()

  return (
    <ProgressSteps currentStep={currentStep}>
      <NumberedStep
        title={
          approvedAmount! >= amountToApprove
            ? `${getCurrencySymbol(token)} approved`
            : `Approve ${getCurrencySymbol(token)}`
        }
        isPending={
          isApprovalTxPending ||
          isFetchingApprovedAmount ||
          isWaitingForApproveTxReceipt
        }
        pendingText={
          isWaitingForApproveTxReceipt || isApprovalTxPending
            ? "Waiting for transaction..."
            : "Checking approval..."
        }
      />
      <NumberedStep title={callTransactionStepTitle} />
    </ProgressSteps>
  )
}

type ApproveAndCallProgressStepsSubmitButtonProps = Exclude<
  ButtonProps,
  "onClick"
>

export function ApproveAndCallProgressStepsSubmitButton({
  children,
  isLoading,
  ...restProps
}: ApproveAndCallProgressStepsSubmitButtonProps) {
  const {
    onSubmitStep,
    isFetchingApprovedAmount,
    isApprovalTxPending,
    isWaitingForApproveTxReceipt,
  } = useApproveAndCallProgressStepsContext()

  return (
    <Button
      {...restProps}
      isLoading={
        isFetchingApprovedAmount ||
        isApprovalTxPending ||
        isWaitingForApproveTxReceipt ||
        isLoading
      }
      onClick={onSubmitStep}
    >
      {children}
    </Button>
  )
}
